import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ShieldCheck, CreditCard, ArrowLeft, Sparkles, Loader2, Plus, Minus } from "lucide-react";

const FALLBACK_BULK_TIERS = {
  pro_1y: [[1, 4, 199], [5, 9, 179], [10, 24, 169], [25, 49, 159], [50, null, 149]],
  lifetime: [[1, 4, 499], [5, 9, 449], [10, 24, 419], [25, 49, 399], [50, 99, 379]],
};

const FALLBACK_PLANS = {
  pro_1y:    { id: "pro_1y",    name: "Pro 1-Year", price_usd: 199, docs: "14,999 documents / year" },
  lifetime:  { id: "lifetime",  name: "Lifetime",   price_usd: 499, docs: "Unlimited documents" },
  refill_1k: { id: "refill_1k", name: "Refill Pack", price_usd: 10,  docs: "1,500 Documents" },
};

function unitPriceFor(tiers, qty) {
  if (!tiers) return null;
  for (const t of tiers) {
    const lo = t[0], hi = t[1], price = t[2];
    if (qty >= lo && (hi === null || qty <= hi)) return price;
  }
  return null;
}

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { planId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(() => FALLBACK_PLANS[planId] || null);
  const [bulkTiers, setBulkTiers] = useState(FALLBACK_BULK_TIERS);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [qty, setQty] = useState(() => {
    const q = parseInt(params.get("qty") || "1", 10);
    return Number.isFinite(q) && q >= 1 ? Math.min(99, q) : 1;
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Guard against unknown / free plans reaching this page.
    if (!FALLBACK_PLANS[planId]) {
      toast.error("Invalid plan");
      navigate("/pricing");
      return;
    }
    // Refresh from server in background (does not block render).
    api.get("/plans").then(r => {
      const found = r.data.plans.find(p => p.id === planId);
      if (found && found.price_usd > 0) setPlan(found);
      if (r.data.bulk_tiers) setBulkTiers(r.data.bulk_tiers);
    }).catch(() => { /* keep fallback plan / tiers on failure */ });
  }, [planId, navigate]);

  const hasBulk = planId === "pro_1y" || planId === "lifetime";
  const tiers = bulkTiers[planId];
  const effectiveQty = hasBulk ? qty : 1;
  const unitPrice = hasBulk ? (unitPriceFor(tiers, effectiveQty) ?? plan?.price_usd ?? 0) : (plan?.price_usd ?? 0);
  const total = unitPrice * effectiveQty;
  const totalSaved = hasBulk && plan ? (plan.price_usd - unitPrice) * effectiveQty : 0;

  const handlePay = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/orders", {
        plan_id: planId,
        customer_name: name.trim(),
        customer_email: email.trim(),
        quantity: effectiveQty,
      });

      // Sandbox path: backend not configured with Razorpay keys → simulate
      if (order.test_mode || !order.razorpay_key_id) {
        const verify = await api.post("/payments/verify", {
          order_id: order.order_id,
          razorpay_order_id: "test_order_" + order.order_id.slice(0, 8),
          razorpay_payment_id: "test_pay_" + order.order_id.slice(0, 8),
          razorpay_signature: "test_signature",
        });
        toast.success("Sandbox payment captured");
        navigate(`/success?token=${verify.data.download_token}&license=${verify.data.license_key}&order=${order.order_id}`);
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Failed to load Razorpay. Check your network.");
        setSubmitting(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: "USD",
        name: "Techno Sort",
        description: `${plan.name} × ${effectiveQty} — one-time purchase`,
        order_id: order.razorpay_order_id,
        prefill: { name, email },
        theme: { color: "#F4A22A" },
        handler: async (resp) => {
          try {
            const verify = await api.post("/payments/verify", {
              order_id: order.order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment verified — license generated");
            navigate(`/success?token=${verify.data.download_token}&license=${verify.data.license_key}&order=${order.order_id}`);
          } catch (err) {
            toast.error("Payment verification failed");
            setSubmitting(false);
          }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      });
      rzp.open();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create order";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  if (!plan) return null;

  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-8 py-24" data-testid="checkout-page">
      <Link to="/pricing" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-10" data-testid="checkout-back">
        <ArrowLeft className="w-4 h-4" /> Back to pricing
      </Link>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Summary */}
        <div className="card-tile p-10">
          <div className="text-xs tracking-[0.25em] uppercase text-neutral-500 mb-2">Order summary</div>
          <div className="font-display font-black text-3xl text-white">{plan.name} License</div>
          <p className="text-neutral-400 text-sm mt-2">{plan.docs}</p>

          {hasBulk && (
            <div className="mt-8 border border-white/10 p-5" data-testid="checkout-qty-block">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">Licenses</div>
                  <div className="text-neutral-500 text-xs mt-1">Bulk discount applied automatically</div>
                </div>
                <div className="flex items-stretch border border-white/20">
                  <button
                    type="button"
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    disabled={qty <= 1}
                    data-testid="checkout-qty-decrement"
                    className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-40"
                    aria-label="Decrease quantity"
                  ><Minus className="w-4 h-4" /></button>
                  <div className="w-14 flex items-center justify-center font-mono text-white text-base border-x border-white/20" data-testid="checkout-qty-value">{qty}</div>
                  <button
                    type="button"
                    onClick={() => setQty(Math.min(99, qty + 1))}
                    disabled={qty >= 99}
                    data-testid="checkout-qty-increment"
                    className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-40"
                    aria-label="Increase quantity"
                  ><Plus className="w-4 h-4" /></button>
                </div>
              </div>
              {totalSaved > 0 && (
                <div className="mt-3 text-[11px] text-[#F4A22A] flex items-center gap-1.5" data-testid="checkout-savings">
                  <Sparkles className="w-3 h-3" /> Saving ${totalSaved} on this order
                </div>
              )}
            </div>
          )}

          <div className="mt-8 space-y-3 border-t border-white/10 pt-6 text-sm">
            <div className="flex justify-between"><span className="text-neutral-400">Plan</span><span className="text-white">{plan.name}</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Quota</span><span className="text-white">{plan.docs}</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Quantity</span><span className="text-white">{effectiveQty} license{effectiveQty > 1 ? "s" : ""}</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Unit price</span><span className="text-white">${unitPrice} USD</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Currency</span><span className="text-white">USD</span></div>
          </div>

          <div className="mt-8 flex items-baseline justify-between border-t border-white/10 pt-6">
            <span className="text-neutral-400 text-sm">Total</span>
            <span className="font-display font-black text-4xl text-white" data-testid="checkout-total">${total}<span className="text-neutral-500 text-base ml-1">USD</span></span>
          </div>

          <div className="mt-8 flex items-center gap-3 text-xs text-neutral-500">
            <ShieldCheck className="w-4 h-4 text-[#1E5BA8]" /> Secured by Razorpay · 256-bit SSL · PCI-DSS
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handlePay} className="card-tile p-10" data-testid="checkout-form">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-neutral-300 border border-white/15 px-3 py-1.5 bg-white/5 mb-6">
            <Sparkles className="w-3 h-3 text-[#F4A22A]" /> Powered by AI
          </div>
          <div className="font-display font-black text-2xl text-white mb-1">Your details</div>
          <p className="text-neutral-400 text-sm mb-8">License key + download will be emailed here within 60 seconds of payment.</p>

          <label className="block text-xs tracking-[0.2em] uppercase text-neutral-400 mb-2">Full name</label>
          <input
            data-testid="checkout-input-name"
            value={name} onChange={(e) => setName(e.target.value)}
            type="text" required
            className="w-full bg-black border border-white/20 text-white px-4 py-3 mb-5 focus:outline-none focus:border-[#F4A22A]"
          />

          <label className="block text-xs tracking-[0.2em] uppercase text-neutral-400 mb-2">Email</label>
          <input
            data-testid="checkout-input-email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            type="email" required
            className="w-full bg-black border border-white/20 text-white px-4 py-3 mb-8 focus:outline-none focus:border-[#F4A22A]"
          />

          <button
            type="submit"
            disabled={submitting}
            data-testid="checkout-pay-button"
            className="btn-primary w-full py-4 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Pay ${total} USD with Razorpay
          </button>

          <div className="mt-6 flex items-center gap-2 text-[11px] text-neutral-500">
            <Lock className="w-3 h-3" /> No card details are stored on our servers. Razorpay handles all payment data.
          </div>
        </form>
      </div>
    </section>
  );
}
